import React from "react";
import {
  Avatar,
  Button,
  Hide,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiChevronDown, FiExternalLink } from "react-icons/fi";
import { Link as GatsbyLink } from "gatsby";
import { IS_PROD, OdysseyUser } from "@utils";
import { ResetProgress } from "../ResetProgress";
import { differenceInMinutes } from "date-fns";
import { useCallbackUrl } from "../MultiForm";
import { useMount } from "react-use";
import { useUpdateUserMutation } from "@graphql";

type UserMenuProps = {
  user: OdysseyUser;
};

export function UserMenu({ user }: UserMenuProps) {
  const callbackUrl = useCallbackUrl();

  const onLogout = () => {
    if (!IS_PROD) {
      window.addEventListener("focus", () => window.location.reload());
    }
  };

  const [updateUser] = useUpdateUserMutation();
  const menuBg = useColorModeValue("silver.200", "black.100");
  useMount(() => {
    const isEnterpriseUser = user.memberships.some(
      (membership) => membership.account.currentPlan.tier === "ENTERPRISE"
    );
    if (
      // did the user sign up with GitHub or SSO?
      (isEnterpriseUser || user.githubUsername) &&
      // was the account created in the last minute?
      differenceInMinutes(Date.now(), new Date(user.createdAt)) < 6
    ) {
      // update user with "odyssey" referrer
      updateUser({
        variables: {
          id: user.id,
          email: user.email,
          fullName: user.fullName,
          referrer: `odyssey-${isEnterpriseUser ? "sso" : "gh"}`,
        },
      });
    }
  });

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        variant="ghost"
        fontSize="md"
        fontWeight="normal"
        flexShrink={0}
        leftIcon={
          <Avatar
            size="sm"
            src={user.avatarUrl || undefined}
            name={user.fullName}
            mr={{ base: -2, md: 0 }}
          />
        }
        rightIcon={<FiChevronDown />}
        pl={0}
        rounded="full"
      >
        <Hide below="md">{user.fullName}</Hide>
      </MenuButton>
      <MenuList bg={menuBg}>
        <MenuItem
          as="a"
          target="_blank"
          href="https://studio.apollographql.com/user-settings"
          icon={<FiExternalLink />}
        >
          Account settings
        </MenuItem>
        <GatsbyLink to="/dashboard/courses">
          <MenuItem>My courses</MenuItem>
        </GatsbyLink>
        <GatsbyLink to="/dashboard/certifications">
          <MenuItem>My certifications</MenuItem>
        </GatsbyLink>
        <MenuDivider />
        {!IS_PROD && <ResetProgress user={user} />}
        <MenuItem
          as="a"
          onClick={onLogout}
          target={IS_PROD ? undefined : "_blank"}
          href={`${process.env.GATSBY_API_URL}/logout?cb=${callbackUrl}`}
        >
          Log out
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
